import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, AlertCircle, PenLine, ChevronRight, Send } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { statusLabel, statusTone, bytesToHuman, fmtDateTime, TAX_FILE_LABELS, SIGNATURE_LABELS } from '../../lib/docs';
import SpecialistTimelineManager from '../../components/SpecialistTimelineManager';
import { toast } from 'sonner';

const TAX_FILE_OPTIONS = Object.entries(TAX_FILE_LABELS);

export default function AdminMemberWorkspacePage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tfNotes, setTfNotes] = useState('');
  const [tfStatus, setTfStatus] = useState('');
  const [reqForm, setReqForm] = useState({ category_id: '', message: '' });
  const [sigForm, setSigForm] = useState({ title: '', message: '', document_id: '' });
  const uploadRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.specialistMemberWorkspace(id);
      setData(d);
      setTfNotes(d.tax_file.notes || '');
      setTfStatus(d.tax_file.status);
      if (d.categories[0]) setReqForm((r) => ({ ...r, category_id: d.categories[0].id }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load workspace');
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveTaxFile = async () => {
    try {
      setBusy(true);
      await api.specialistTaxFileStatus(id, data.tax_file.tax_year, { status: tfStatus, notes: tfNotes });
      toast.success('Tax file updated');
      await load();
    } catch { toast.error('Could not update tax file'); } finally { setBusy(false); }
  };

  const requestDoc = async () => {
    if (!reqForm.category_id) return toast.error('Choose a category');
    try {
      await api.specialistRequestDoc({
        member_user_id: id, category_id: reqForm.category_id,
        tax_year: data.tax_file.tax_year, message: reqForm.message,
      });
      toast.success('Request sent');
      setReqForm({ ...reqForm, message: '' });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send request'); }
  };

  const createSignature = async () => {
    if (!sigForm.title.trim()) return toast.error('Add a title');
    try {
      await api.specialistCreateSignature({
        member_user_id: id, tax_year: data.tax_file.tax_year,
        title: sigForm.title, message: sigForm.message,
        document_id: sigForm.document_id || null,
      });
      toast.success('Signature request sent');
      setSigForm({ title: '', message: '', document_id: '' });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send signature request'); }
  };

  const uploadFor = async (file) => {
    if (!file) return;
    const cat = data.categories.find((c) => c.code === 'other') || data.categories[0];
    const fd = new FormData();
    fd.append('file', file);
    fd.append('tax_year', String(data.tax_file.tax_year));
    fd.append('category_id', cat.id);
    fd.append('description', 'Uploaded by specialist');
    try {
      setBusy(true);
      await api.specialistUploadForMember(id, fd);
      toast.success('Uploaded on behalf of member');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  if (loading || !data) return (<AdminLayout title="Workspace"><div className="py-20 text-center text-neutral-500">Loading…</div></AdminLayout>);

  const { member, tax_file, documents, categories, document_requests, signature_requests } = data;

  return (
    <AdminLayout title={member.name} subtitle={`${member.email} · Tax file ${tax_file.tax_year}`} actions={
      <Link to="/admin/documents" className="inline-flex items-center gap-1 text-[12.5px] text-neutral-500 hover:text-[#c89200]">
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </Link>
    }>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Timeline manager — vertical workflow tracker */}
          <SpecialistTimelineManager memberId={id} taxYear={tax_file.tax_year} />

          {/* Tax file status */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[15px] font-bold text-[#14110f] mb-4">Tax file status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <select data-testid="tf-status" value={tfStatus} onChange={(e) => setTfStatus(e.target.value)}
                      className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none">
                {TAX_FILE_OPTIONS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button data-testid="tf-save" onClick={saveTaxFile} disabled={busy}
                      className="bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold rounded-md text-[13px] px-4">
                Save tax file
              </button>
            </div>
            <textarea data-testid="tf-notes" value={tfNotes} onChange={(e) => setTfNotes(e.target.value)} rows={3}
                      placeholder="Internal notes about this tax file…"
                      className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13px] focus:border-[#f5b300] outline-none" />
          </div>

          {/* Documents list */}
          <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-neutral-100 flex items-center justify-between">
              <h3 className="text-[15px] font-bold text-[#14110f]">Documents on file</h3>
              <div>
                <button onClick={() => uploadRef.current?.click()} disabled={busy}
                        className="inline-flex items-center gap-2 bg-[#14110f] text-white text-[12.5px] font-semibold px-3.5 py-2 rounded-md hover:bg-[#291d12]">
                  <Upload className="w-3.5 h-3.5" /> Upload on behalf
                </button>
                <input ref={uploadRef} type="file" className="hidden" onChange={(e) => uploadFor(e.target.files?.[0])} />
              </div>
            </div>
            {documents.length === 0 ? <p className="px-5 py-10 text-center text-[13px] text-neutral-500">No documents yet for this tax year.</p>
              : <table className="w-full text-[13.5px]" data-testid="member-docs-table">
                  <thead className="bg-neutral-50 text-[11.5px] uppercase tracking-wider text-neutral-600">
                    <tr><th className="text-left px-5 py-2.5">Document</th><th className="text-left px-5 py-2.5">Category</th><th className="text-left px-5 py-2.5">Status</th><th className="text-right px-5 py-2.5">Size</th><th></th></tr>
                  </thead>
                  <tbody>
                    {documents.map((d) => (
                      <tr key={d.id} className="border-t border-neutral-100 hover:bg-neutral-50">
                        <td className="px-5 py-3">
                          <p className="font-semibold text-[#14110f] truncate">{d.original_name}</p>
                          <p className="text-[11.5px] text-neutral-500">{fmtDateTime(d.uploaded_at)} by {d.uploaded_by_name}</p>
                        </td>
                        <td className="px-5 py-3 text-neutral-700">{d.category_name}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusTone(d.status)}`}>{statusLabel(d.status)}</span>
                        </td>
                        <td className="px-5 py-3 text-right text-neutral-500 text-[12px]">{bytesToHuman(d.size)}</td>
                        <td className="px-5 py-3 text-right">
                          <Link to={`/admin/documents/${d.id}`} className="text-[12px] font-semibold text-[#c89200] hover:underline inline-flex items-center gap-1">
                            Review <ChevronRight className="w-3.5 h-3.5" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </div>

          {/* Request docs */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[15px] font-bold text-[#14110f] mb-4 flex items-center gap-2"><AlertCircle className="w-4 h-4 text-amber-600" /> Request missing document</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select data-testid="req-category" value={reqForm.category_id} onChange={(e) => setReqForm({ ...reqForm, category_id: e.target.value })}
                      className="px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none">
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input data-testid="req-message" value={reqForm.message} onChange={(e) => setReqForm({ ...reqForm, message: e.target.value })}
                     placeholder="Message to member…"
                     className="md:col-span-2 px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
            </div>
            <div className="mt-3 flex justify-end">
              <button data-testid="req-send" onClick={requestDoc} className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] font-semibold px-4 py-2 rounded-md text-[12.5px]">
                <Send className="w-3.5 h-3.5" /> Send request
              </button>
            </div>
            {document_requests.length > 0 && (
              <ul className="mt-5 divide-y divide-neutral-100" data-testid="req-list">
                {document_requests.map((r) => (
                  <li key={r.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-[13px] font-semibold text-[#14110f]">{r.category?.name}</p>
                      {r.message && <p className="text-[12px] text-neutral-600">{r.message}</p>}
                      <p className="text-[11px] text-neutral-400">{fmtDateTime(r.requested_at)} · {r.status}</p>
                    </div>
                    {r.status === 'open' && (
                      <button onClick={() => api.specialistCloseRequest(r.id).then(load)} className="text-[12px] text-neutral-500 hover:text-rose-600 underline">Close</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Signature request */}
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[15px] font-bold text-[#14110f] mb-4 flex items-center gap-2"><PenLine className="w-4 h-4 text-fuchsia-600" /> Request signature</h3>
            <div className="space-y-3">
              <input data-testid="sig-title" value={sigForm.title} onChange={(e) => setSigForm({ ...sigForm, title: e.target.value })}
                     placeholder="Signature title (e.g. T1013 authorization)"
                     className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none" />
              <textarea data-testid="sig-message" value={sigForm.message} onChange={(e) => setSigForm({ ...sigForm, message: e.target.value })}
                        rows={2} placeholder="Instructions for the member…"
                        className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13px] focus:border-[#f5b300] outline-none" />
              <select data-testid="sig-doc" value={sigForm.document_id} onChange={(e) => setSigForm({ ...sigForm, document_id: e.target.value })}
                      className="w-full px-3 py-2.5 rounded-md border border-neutral-200 text-[13.5px] focus:border-[#f5b300] outline-none">
                <option value="">(Optional) Link to existing document…</option>
                {documents.map((d) => <option key={d.id} value={d.id}>{d.original_name}</option>)}
              </select>
              <div className="flex justify-end">
                <button data-testid="sig-send" onClick={createSignature} className="inline-flex items-center gap-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold px-4 py-2 rounded-md text-[12.5px]">
                  <PenLine className="w-3.5 h-3.5" /> Send signature request
                </button>
              </div>
            </div>
            {signature_requests.length > 0 && (
              <ul className="mt-5 divide-y divide-neutral-100" data-testid="sig-list">
                {signature_requests.map((s) => (
                  <li key={s.id} className="py-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-[13px] font-semibold text-[#14110f]">{s.title}</p>
                      <p className="text-[11px] text-neutral-400">{fmtDateTime(s.requested_at)} · {SIGNATURE_LABELS[s.status] || s.status}</p>
                    </div>
                    {s.status !== 'signed' && s.status !== 'cancelled' && (
                      <button onClick={() => api.specialistSignatureStatus(s.id, 'cancelled').then(load)}
                              className="text-[12px] text-neutral-500 hover:text-rose-600 underline">Cancel</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <aside className="space-y-5">
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[14px] font-bold text-[#14110f] mb-2">Member</h3>
            <p className="text-[14px] font-semibold text-[#14110f]">{member.name}</p>
            <p className="text-[12.5px] text-neutral-500">{member.email}</p>
            {member.phone && <p className="text-[12.5px] text-neutral-500">{member.phone}</p>}
          </div>
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h3 className="text-[14px] font-bold text-[#14110f] mb-3">Required checklist</h3>
            <ul className="space-y-2">
              {categories.filter((c) => c.required).map((c) => {
                const have = documents.some((d) => d.category_id === c.id);
                return (
                  <li key={c.id} className="flex items-center justify-between text-[12.5px]">
                    <span className="text-neutral-700">{c.name}</span>
                    <span className={`text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${have ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{have ? 'On file' : 'Missing'}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>
      </div>
    </AdminLayout>
  );
}
